import { Award, Target, Heart, Users, Lightbulb } from 'lucide-react';
import { useReveal } from '../hooks/useReveal';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_formando-lideres/artifacts/y7eqj8yu_logo%20rise.jpg';
const DIRECTOR_PHOTO = 'https://customer-assets.emergentagent.com/job_formando-lideres/artifacts/e107zbnp_PHOTO-2026-04-15-20-56-21.jpg';

export const SobreNosotrosPage = () => {
  const dirImgRef = useReveal();
  const dirTextRef = useReveal();
  const dirQuoteRef = useReveal();
  return (
  <div className="pt-20" data-testid="sobre-nosotros-page">
    {/* Hero with GOLD accent */}
    <section className="py-20 md:py-28 bg-gradient-to-br from-[#43b6b8] to-[#359a9c] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none flex items-center justify-center">
        <img src={LOGO_URL} alt="" className="h-[400px] w-auto" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 md:px-12 text-center">
        <span className="inline-block bg-[#d3b163] text-white px-5 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-6">
          Sobre Nosotros
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
          Educando con propósito en Dorado
        </h1>
        <p className="text-lg text-white/90 max-w-2xl mx-auto">
          Conoce la historia y filosofía que guía nuestro centro educativo.
        </p>
      </div>
    </section>

    {/* Historia */}
    <section className="py-20 md:py-28" data-testid="historia-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d3b163] mb-4 block">Nuestra Historia</span>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 mb-6">
              Un compromiso con la educación personalizada
            </h2>
            <p className="text-base text-stone-700 leading-relaxed mb-4">
              Rise & Shine Learning Zone nació del deseo de crear un espacio donde cada niño pueda recibir la atención personalizada que merece. Como centro educativo multidisciplinario en Dorado, Puerto Rico, nos especializamos en apoyo académico para estudiantes K-12.
            </p>
            <p className="text-base text-stone-700 leading-relaxed mb-4">
              Nuestro equipo está formado por educadores apasionados y certificados que entienden que cada estudiante es único. Combinamos metodologías innovadoras con un enfoque cálido y familiar para crear un ambiente donde los niños se sienten seguros y motivados.
            </p>
            <p className="text-base text-stone-700 leading-relaxed">
              Nos enorgullece ser parte del crecimiento académico y personal de nuestros estudiantes, viendo cómo ganan confianza y desarrollan amor por el aprendizaje.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative">
            <img
              src="https://customer-assets.emergentagent.com/job_formando-lideres/artifacts/dg31l0gq_2ff8757f-3988-4aab-939d-8e91c0d638f2.jpeg"
              alt="Educadora trabajando uno-a-uno con estudiante en Rise & Shine"
              className="w-full h-full object-cover object-center"
              loading="lazy"
            />
            <div className="absolute bottom-4 right-4 bg-white rounded-2xl p-2 shadow-lg">
              <img src={LOGO_URL} alt="Rise & Shine" className="h-12 w-auto" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Misión Visión Valores */}
    <section className="py-20 md:py-28 bg-[#F3F0EA]" data-testid="mision-vision-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-t-4 border-[#d3b163]" data-testid="mision-card">
            <Target className="w-12 h-12 text-[#43b6b8] mb-6" />
            <h3 className="text-2xl font-semibold text-stone-900 mb-4">Misión</h3>
            <p className="text-base text-stone-700 leading-relaxed">
              Proveer apoyo educativo personalizado de calidad que fortalezca las habilidades académicas, la confianza y el amor por el aprendizaje en cada estudiante, creando un ambiente seguro y motivador en Dorado, Puerto Rico.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-t-4 border-[#d3b163]" data-testid="vision-card">
            <Award className="w-12 h-12 text-[#43b6b8] mb-6" />
            <h3 className="text-2xl font-semibold text-stone-900 mb-4">Visión</h3>
            <p className="text-base text-stone-700 leading-relaxed">
              Ser el centro educativo de referencia en Dorado y áreas cercanas, reconocido por nuestra calidad en apoyo académico personalizado, innovación pedagógica y compromiso genuino con el éxito de cada estudiante.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-t-4 border-[#d3b163]" data-testid="valores-card">
            <Heart className="w-12 h-12 text-[#43b6b8] mb-6" />
            <h3 className="text-2xl font-semibold text-stone-900 mb-4">Valores</h3>
            <ul className="text-base text-stone-700 leading-relaxed space-y-2">
              <li>• Calidad educativa</li>
              <li>• Atención personalizada</li>
              <li>• Empatía y respeto</li>
              <li>• Compromiso familiar</li>
              <li>• Innovación pedagógica</li>
              <li>• Integridad profesional</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* Filosofía */}
    <section className="py-20 md:py-28" data-testid="approach-section">
      <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#d3b163] mb-4 block">Nuestro Enfoque</span>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 mb-6">
            Filosofía Educativa
          </h2>
        </div>

        <div className="bg-white p-8 md:p-12 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="space-y-6 text-base text-stone-700 leading-relaxed">
            <div className="flex gap-4">
              <Lightbulb className="w-6 h-6 text-[#d3b163] flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-stone-900 mb-2">Aprendizaje Personalizado</h4>
                <p>Reconocemos que cada estudiante tiene su propio ritmo y estilo de aprendizaje. Adaptamos nuestras estrategias para maximizar el potencial de cada niño.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Users className="w-6 h-6 text-[#d3b163] flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-stone-900 mb-2">Enfoque Familiar</h4>
                <p>Trabajamos en colaboración estrecha con las familias, manteniéndolas informadas y siendo socios activos en el progreso educativo de sus hijos.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <Heart className="w-6 h-6 text-[#d3b163] flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-stone-900 mb-2">Desarrollo Integral</h4>
                <p>Más allá del rendimiento académico, fomentamos la confianza, la autoestima y las habilidades socioemocionales que son fundamentales para el éxito en la vida.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Sobre la Directora */}
    <section className="py-24 md:py-32 bg-gradient-to-b from-[#F3F0EA] to-[#FDFBF7]" data-testid="directora-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#d3b163] mb-5 block">Liderazgo</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-stone-900 mb-5 leading-tight">
            Sobre la Directora
          </h2>
          <div className="w-16 h-[3px] bg-[#d3b163] mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5 flex justify-center">
            <div ref={dirImgRef} className="reveal-scale relative">
              {/* Decorative glow */}
              <div className="absolute -inset-4 bg-gradient-to-br from-[#d3b163]/40 via-[#43b6b8]/30 to-[#d3b163]/40 rounded-full blur-2xl"></div>
              {/* Gold ring */}
              <div className="absolute -inset-2 bg-gradient-to-br from-[#d3b163] to-[#43b6b8] rounded-full"></div>
              <img
                src={DIRECTOR_PHOTO}
                alt="Doctora Melissa Núñez Robles"
                className="relative w-80 h-80 md:w-[400px] md:h-[400px] rounded-full object-cover ring-[6px] ring-white shadow-2xl"
                style={{ objectPosition: '65% 30%' }}
                data-testid="director-photo"
              />
              <div className="absolute bottom-3 right-3 md:bottom-5 md:right-5 bg-white rounded-full p-1.5 shadow-lg">
                <div className="bg-[#d3b163] text-white rounded-full px-5 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em]">
                  Directora
                </div>
              </div>
            </div>
          </div>

          <div ref={dirTextRef} style={{ transitionDelay: '80ms' }} className="reveal md:col-span-7 space-y-5 text-base md:text-[17px] text-stone-700 leading-relaxed">
            <div>
              <h3 className="text-3xl md:text-4xl font-semibold text-stone-900 leading-tight">Dra. Melissa Núñez Robles</h3>
              <p className="text-sm font-semibold text-[#43b6b8] uppercase tracking-[0.18em] mt-2">
                Fundadora y Directora de Rise & Shine Learning Zone
              </p>
              <div className="w-12 h-[3px] bg-[#d3b163] rounded-full mt-5"></div>
            </div>
            <p>
              Melissa Núñez Robles es una educadora puertorriqueña con más de diecisiete años de experiencia en el campo de la educación y una trayectoria marcada por su compromiso con la inclusión, la diversidad y el aprendizaje significativo.
            </p>
            <p>
              Fundadora y directora de Rise & Shine Learning Zone LLC en Dorado, Puerto Rico, Melissa ha dedicado su vida profesional a crear espacios educativos donde cada niño pueda desarrollar su máximo potencial a través de la educación individualizada y el acompañamiento respetuoso.
            </p>
            <p>
              Es Terapista Educativa Clínica, evaluadora de métodos alternos y posee certificaciones en autismo y neurociencia cognitiva aplicada a la educación. Su preparación académica incluye una maestría en ciencias económicas y administrativas y un bachillerato en educación elemental con especialidad en inglés.
            </p>
            <p>
              Actualmente, posee un doctorado en Educación Especial, donde investigó sobre las barreras de acceso a los servicios de intervención temprana para niños con trastorno del espectro autista en Puerto Rico y Latinoamérica.
            </p>
            <p>
              Más allá de su práctica profesional, Melissa se destaca por su pasión por apoyar a las familias que enfrentan diagnósticos complejos en sus hijos, guiándolas con sensibilidad, información confiable y una profunda visión humanista. Esta obra refleja su compromiso personal y profesional con la aceptación, la comprensión y el acompañamiento empático.
            </p>
            <p ref={dirQuoteRef} style={{ transitionDelay: '160ms' }} className="reveal text-lg md:text-xl font-medium text-stone-900 italic pt-3 border-t border-[#d3b163]/30 mt-6">
              Para Melissa, educar es un acto de amor, propósito y transformación. Su misión continúa siendo abrir caminos para que cada niño, familia y comunidad pueda brillar y alcanzar su propio horizonte.
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
  );
};
